import { useTranslation } from 'react-i18next';

import { Label } from '@/shared/ui/components/label';
import type { Room } from '@/modules/venues/rooms';

export interface RoomCoverage {
  allRooms: boolean;
  roomIds: string[];
}

interface RoomCoverageFieldProps {
  allRooms: boolean;
  roomIds: string[];
  rooms: Room[];
  onChange: (next: RoomCoverage) => void;
}

/**
 * The room-coverage control for a pass form (PRD #164 stories 13-16): an `allRooms` toggle and,
 * when it is off, a multi-select of the Venue's rooms. All-rooms covers everything and
 * auto-includes rooms added later (so no explicit ids), otherwise the manager picks at least one
 * room — the backend stays authoritative on the `>=1`-room rule (ADR 0007). A single
 * `{ allRooms, roomIds }` value flows out so the host page never reasons about the toggle/list
 * interplay.
 */
export function RoomCoverageField({ allRooms, roomIds, rooms, onChange }: RoomCoverageFieldProps) {
  const { t } = useTranslation('common');

  function toggleRoom(roomId: string, checked: boolean) {
    const next = checked ? [...roomIds, roomId] : roomIds.filter((id) => id !== roomId);
    onChange({ allRooms: false, roomIds: next });
  }

  return (
    <fieldset className="grid gap-3 rounded-md border p-4">
      <legend className="px-1 text-sm font-medium">{t('products.form.rooms')}</legend>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={allRooms}
          onChange={(event) => onChange({ allRooms: event.target.checked, roomIds })}
        />
        {t('products.form.allRooms')}
      </label>

      {!allRooms && (
        <div className="grid gap-2">
          <Label className="text-muted-foreground">{t('products.form.pickRooms')}</Label>
          {rooms.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('products.form.noRooms')}</p>
          ) : (
            <ul className="grid gap-1">
              {rooms.map((room) => (
                <li key={room.id}>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      aria-label={room.name}
                      checked={roomIds.includes(room.id)}
                      onChange={(event) => toggleRoom(room.id, event.target.checked)}
                    />
                    {room.name}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </fieldset>
  );
}
